"use client";

import { Button } from "../../";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../";
import { ScrollArea } from "../../";
import { cn } from "../../utils";
import { IconChevronDown, IconPaperclip } from "@tabler/icons-react";
import type { ComponentProps } from "react";
import { Spinner } from "../../components/spinner/Spinner";

export interface QueueMessagePart {
  type: string;
  text?: string;
  url?: string;
  filename?: string;
  mediaType?: string;
}

export interface QueueMessage {
  id: string;
  parts: QueueMessagePart[];
}

export interface QueueTodo {
  id: string;
  title: string;
  description?: string;
  status?: "pending" | "completed";
}

export type QueueItemProps = ComponentProps<"li">;

export const QueueItem = ({ className, ...props }: QueueItemProps) => (
  <li
    className={cn(
      "al-group al-flex al-flex-col al-gap-1 al-rounded-md al-px-3 al-py-1 al-text-sm al-transition-colors hover:al-bg-muted even:al-bg-gray-50",
      className,
    )}
    {...props}
  />
);

export type QueueItemIndicatorProps = ComponentProps<"span"> & {
  status?: "pending" | "in_progress" | "completed";
};

export const QueueItemIndicator = ({
  status = "pending",
  className,
  ...props
}: QueueItemIndicatorProps) => {
  if (status === "in_progress") {
    return (
      <Spinner size="xs" className="al-border-blue-600 al-border-t-blue-300" />
    );
  }

  return (
    <span
      className={cn(
        "al-mt-0.5 al-inline-block al-size-2.5 al-rounded-full",
        status === "completed" &&
          "al-border al-border-muted-foreground/20 al-bg-muted-foreground/10",
        status === "pending" && "al-border al-border-muted-foreground/50",
        className,
      )}
      {...props}
    />
  );
};

export type QueueItemContentProps = ComponentProps<"span"> & {
  status?: "pending" | "in_progress" | "completed";
};

export const QueueItemContent = ({
  status = "pending",
  className,
  ...props
}: QueueItemContentProps) => {
  return (
    <span
      className={cn(
        "al-line-clamp-1 al-grow al-break-words",
        status === "completed" && "al-text-muted-foreground/50 al-line-through",
        status === "in_progress" && "al-text-blue-600 al-font-medium",
        status === "pending" && "al-text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};

export type QueueItemDescriptionProps = ComponentProps<"div"> & {
  status?: "pending" | "in_progress" | "completed";
};

export const QueueItemDescription = ({
  status = "pending",
  className,
  ...props
}: QueueItemDescriptionProps) => (
  <div
    className={cn(
      "al-ml-6 al-text-xs",
      status === "completed" && "al-text-muted-foreground/40 al-line-through",
      status === "in_progress" && "al-text-blue-500",
      status === "pending" && "al-text-muted-foreground",
      className,
    )}
    {...props}
  />
);

export type QueueItemActionsProps = ComponentProps<"div">;

export const QueueItemActions = ({
  className,
  ...props
}: QueueItemActionsProps) => (
  <div className={cn("al-flex al-gap-1", className)} {...props} />
);

export type QueueItemActionProps = Omit<
  ComponentProps<typeof Button>,
  "variant" | "size"
>;

export const QueueItemAction = ({
  className,
  ...props
}: QueueItemActionProps) => (
  <Button
    className={cn(
      "al-size-auto al-rounded al-p-1 al-text-muted-foreground al-opacity-0 al-transition-opacity hover:al-bg-muted-foreground/10 hover:al-text-foreground group-hover:al-opacity-100",
      className,
    )}
    size="icon"
    type="button"
    variant="ghost"
    {...props}
    title="queue"
  />
);

export type QueueItemAttachmentProps = ComponentProps<"div">;

export const QueueItemAttachment = ({
  className,
  ...props
}: QueueItemAttachmentProps) => (
  <div
    className={cn("al-mt-1 al-flex al-flex-wrap al-gap-2", className)}
    {...props}
  />
);

export type QueueItemImageProps = ComponentProps<"img">;

export const QueueItemImage = ({
  className,
  ...props
}: QueueItemImageProps) => (
  <img
    alt=""
    className={cn(
      "al-h-8 al-w-8 al-rounded al-border al-object-cover",
      className,
    )}
    height={32}
    width={32}
    {...props}
  />
);

export type QueueItemFileProps = ComponentProps<"span">;

export const QueueItemFile = ({
  children,
  className,
  ...props
}: QueueItemFileProps) => (
  <span
    className={cn(
      "al-flex al-items-center al-gap-1 al-rounded al-border al-bg-muted al-px-2 al-py-1 al-text-xs",
      className,
    )}
    {...props}
  >
    <IconPaperclip size={12} />
    <span className="al-max-w-[100px] al-truncate">{children}</span>
  </span>
);

export type QueueListProps = ComponentProps<typeof ScrollArea>;

export const QueueList = ({
  children,
  className,
  ...props
}: QueueListProps) => (
  <ScrollArea className={cn("al-mt-2 al--mb-1", className)} {...props}>
    <div className="al-max-h-40 al-pr-4">
      <ul>{children}</ul>
    </div>
  </ScrollArea>
);

// QueueSection - collapsible section container
export type QueueSectionProps = ComponentProps<typeof Collapsible>;

export const QueueSection = ({
  className,
  defaultOpen = true,
  ...props
}: QueueSectionProps) => (
  <Collapsible className={cn(className)} defaultOpen={defaultOpen} {...props} />
);

// QueueSectionTrigger - section header/trigger
export type QueueSectionTriggerProps = ComponentProps<"button">;

export const QueueSectionTrigger = ({
  children,
  className,
  ...props
}: QueueSectionTriggerProps) => (
  <CollapsibleTrigger asChild>
    <button
      className={cn(
        "al-group al-flex al-w-full al-items-center al-justify-between al-rounded-md al-bg-muted/100 al-px-3 al-py-2 al-text-left al-font-medium al-text-muted-foreground al-text-sm al-transition-colors hover:al-bg-muted",
        className,
      )}
      type="button"
      {...props}
    >
      {children}
    </button>
  </CollapsibleTrigger>
);

// QueueSectionLabel - label content with icon and count
export type QueueSectionLabelProps = ComponentProps<"span"> & {
  count?: number;
  label: string;
  icon?: React.ReactNode;
};

export const QueueSectionLabel = ({
  count,
  label,
  icon,
  className,
  ...props
}: QueueSectionLabelProps) => (
  <span
    className={cn("al-flex al-items-center al-gap-2", className)}
    {...props}
  >
    <IconChevronDown className="al-size-4 al-transition-transform group-data-[state=closed]:al--rotate-90" />
    {icon}
    <span>
      {count} {label}
    </span>
  </span>
);

// QueueSectionContent - collapsible content area
export type QueueSectionContentProps = ComponentProps<
  typeof CollapsibleContent
>;

export const QueueSectionContent = ({
  className,
  ...props
}: QueueSectionContentProps) => (
  <CollapsibleContent className={cn(className)} {...props} />
);

export type QueueProps = ComponentProps<"div">;

export const Queue = ({ className, ...props }: QueueProps) => (
  <div
    className={cn(
      "al-flex al-flex-col al-gap-2 al-rounded-md al-border al-border-border al-bg-background al-px-3 al-pt-2 al-pb-2 al-shadow-xs",
      className,
    )}
    {...props}
  />
);
